import type { Weather } from '@obv/api-client';
import {
  EVERYONE, buildAgenda, type HomeWidgetId, clockFace, eyebrowDate, formatPressure, formatTime, formatVisibility, formatWind, greeting, longDate, plansForDay,
  precipLabel, shortWeekday, sparkline, toUnit, weekBars,
} from '@obv/core';
import { useEffect, useRef, type CSSProperties } from 'react';
import { useLocation } from 'wouter';
import { personColor } from '@obv/ui';
import { newMutationKey, usePlanWindow, useUpdatePlan } from '@/queries/hooks';
import { memberLabel, useDisplay } from '../context';
import { WeatherIcon } from '../icons';
import { Avatar, Card, ScreenHeader, TapToOpen, Unavailable } from '../ui';
import { ClockStatus } from './ClockStatus';
import { ExchangeWidget, KanjiSlideshowWidget, VerseWidget } from './ContentWidgets';
import { CalendarWidget } from './CalendarWidget';
import { WeatherHeroCard } from './DashboardParts';
import { UpdatedPill, useWeatherState } from './weatherState';
import { CustomizableHome } from './CustomizableHome';
import { ChoresWidget, HabitsWidget, HijriWidget, NextPrayerWidget, PrayerListWidget, SalatSplitWidget, SalatWidget } from './HomeWidgets';

export type TodayVariant = 'default' | 'outdoor';

export function TodayScreen({ variant = 'default' }: { variant?: TodayVariant }) {
  const ctx = useDisplay();
  const portrait = ctx.orientation === 'portrait';
  if (variant === 'outdoor') {
    // The outdoor scene is a fixed layout built around the air card.
    const grid: CSSProperties = portrait
      ? { gridTemplateColumns: 'minmax(0,1fr)', gridTemplateRows: 'minmax(0,1.1fr) minmax(0,1fr) minmax(0,1fr)', gridTemplateAreas: '"air" "now" "week"' }
      : { gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)', gridTemplateRows: 'minmax(0,1fr) minmax(0,1fr)', gridTemplateAreas: '"air now" "air week"' };
    return (
      <>
        {ctx.chromeHidden ? null : <ScreenHeader eyebrow={eyebrowDate(ctx.clock.dateKey)} right={<UpdatedPill />} />}
        <div className={`${ctx.chromeHidden ? '' : 'mt-4'} grid min-h-0 flex-1`} style={{ ...grid, gridGap: 14 }}>
          <div className="grid min-h-0 min-w-0" style={{ gridArea: 'air' }}>
            <AirCard big compact={false} />
          </div>
          <div className="grid min-h-0 min-w-0" style={{ gridArea: 'now' }}>
            <NowCard tall />
          </div>
          <div className="grid min-h-0 min-w-0" style={{ gridArea: 'week' }}>
            <WeekCard />
          </div>
        </div>
      </>
    );
  }
  return (
    <CustomizableHome
      eyebrow={eyebrowDate(ctx.clock.dateKey)}
      pill={<UpdatedPill />}
      renderWidget={(id, narrow) => <HomeWidget id={id} narrow={narrow} />}
    />
  );
}

/** Renders one home widget; `narrow` slots get the compact variant where one exists. */
export function HomeWidget({ id, narrow }: { id: HomeWidgetId; narrow: boolean }) {
  switch (id) {
    case 'clock':
      return (
        <TapToOpen to="/display/clock">
          <ClockCard narrow={narrow} />
        </TapToOpen>
      );
    case 'now':
      return <NowCard tall={false} />;
    case 'air':
      return <AirCard big={false} compact={narrow} />;
    case 'agenda':
      return <AgendaCard />;
    case 'calendar':
      return <CalendarWidget narrow={narrow} />;
    case 'week':
      return <WeekCard compact={narrow} />;
    case 'weather_hero':
      return <WeatherHeroCard days={narrow ? 4 : 5} />;
    case 'forecast':
      return <ForecastCard narrow={narrow} />;
    case 'chores':
      return <ChoresWidget />;
    case 'habits':
      return <HabitsWidget />;
    case 'salat':
    case 'salat_split':
    case 'salat_next':
    case 'salat_list':
    case 'hijri':
      return <TapToOpen to="/display/prayer">{prayerWidget(id, narrow)}</TapToOpen>;
    case 'verse':
      return <VerseWidget narrow={narrow} />;
    case 'exchange':
      return <ExchangeWidget narrow={narrow} />;
    case 'kanji':
      return <KanjiSlideshowWidget narrow={narrow} />;
    default:
      return null;
  }
}

function prayerWidget(id: 'salat' | 'salat_split' | 'salat_next' | 'salat_list' | 'hijri', narrow: boolean) {
  if (id === 'salat') return <SalatWidget narrow={narrow} />;
  if (id === 'salat_split') return <SalatSplitWidget narrow={narrow} />;
  if (id === 'salat_next') return <NextPrayerWidget narrow={narrow} />;
  if (id === 'salat_list') return <PrayerListWidget narrow={narrow} />;
  return <HijriWidget narrow={narrow} />;
}

/** Today's calendar plans on the home screen, with the NOW line; tap to tick off. */
export function AgendaCard() {
  const ctx = useDisplay();
  const [, navigate] = useLocation();
  const windowQ = usePlanWindow(ctx.clock.dateKey, ctx.boot.server_time);
  const update = useUpdatePlan();
  const dayPlans = windowQ.data ? plansForDay(windowQ.data.items, ctx.clock.dateKey, EVERYONE) : [];
  const items = buildAgenda(dayPlans, { isToday: true, now: ctx.clock.hhmm });
  const left = dayPlans.filter((p) => !p.completed && (p.all_day || (p.time || '') > ctx.clock.hhmm)).length;
  // Keep "now" in view: the morning's finished plans scroll away as the day goes on.
  const listRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef<HTMLDivElement>(null);
  const userScrolledAt = useRef(0);
  // Re-applied after every render (moving the card in customize mode resets its scroll), unless
  // someone scrolled the list by hand in the last 30 seconds.
  useEffect(() => {
    const list = listRef.current;
    const now = nowRef.current;
    if (!list || !now || Date.now() - userScrolledAt.current < 30_000) return;
    const want = Math.max(0, now.offsetTop - 44);
    if (Math.abs(list.scrollTop - want) > 2) list.scrollTop = want;
  });
  return (
    <Card tone={3} className="flex flex-col overflow-hidden px-5 pb-2 pt-[18px]">
      <div className="flex flex-none items-baseline justify-between">
        <h2 className="text-[17px] font-normal">Today’s plans</h2>
        <button type="button" className="tap -my-3 h-[44px] text-[13px] font-medium" onClick={() => navigate('/display/calendar')}>
          {dayPlans.length ? `${left} to go ↗` : 'Calendar ↗'}
        </button>
      </div>
      <div
        ref={listRef}
        className="scroll-y relative mt-1 min-h-0 flex-1"
        onTouchMove={() => (userScrolledAt.current = Date.now())}
        onWheel={() => (userScrolledAt.current = Date.now())}
      >
        {windowQ.isLoading ? (
          <Unavailable>Loading plans…</Unavailable>
        ) : items.length === 0 ? (
          <Unavailable>Nothing planned today.</Unavailable>
        ) : (
          items.map((it, i) => {
            if (it.kind === 'now') {
              return (
                <div key={`now-${i}`} ref={nowRef} className="flex h-[16px] items-center" aria-label="Now">
                  <span className="mr-1 h-[7px] w-[7px] rounded-full" style={{ background: 'var(--now)' }} />
                  <span className="h-[2px] flex-1" style={{ background: 'var(--now)' }} />
                </div>
              );
            }
            if (it.kind === 'allDayHeader') return null;
            const p = it.plan;
            const who = memberLabel(ctx, p.member_id);
            return (
              <button
                key={p.id}
                type="button"
                disabled={p.read_only || p.id.indexOf('tmp-') === 0}
                onClick={() => update.mutate({ id: p.id, body: { completed: !p.completed }, key: newMutationKey() })}
                className="tap flex min-h-[40px] w-full items-center border-b text-left"
                style={{ borderColor: 'var(--line2)', opacity: p.completed ? 0.5 : it.past ? 0.6 : 1 }}
                aria-label={`${p.title}${p.completed ? ', done' : ''}. Tap to ${p.completed ? 'undo' : 'check off'}.`}
              >
                <span className="tabular w-[46px] flex-none text-[12px] text-muted">{p.all_day ? 'All day' : formatTime(p.time!, ctx.prefs.h24)}</span>
                <span className="mr-2 h-[18px] w-[3px] flex-none rounded-[2px]" style={{ background: personColor(who.colorKey) }} />
                <span className="ellipsis min-w-0 flex-1 text-[14px]" style={{ textDecoration: p.completed ? 'line-through' : 'none' }}>
                  {p.title}
                </span>
                <span className="ml-2 flex-none">
                  <Avatar initial={who.initial} size={22} />
                </span>
              </button>
            );
          })
        )}
      </div>
    </Card>
  );
}

// Bold and as large as the slot allows: the time is the one thing read from across the room.
const CLOCK_PX = { wide: 136, portrait: 96, narrow: 84 };

function ClockCard({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const face = clockFace(ctx.clock.hhmm, ctx.prefs.h24);
  const base = narrow ? CLOCK_PX.narrow : ctx.orientation === 'portrait' ? CLOCK_PX.portrait : CLOCK_PX.wide;
  // "12:45 PM" is wider than "12:45"; shrink a little so the suffix still fits the card.
  const size = face.suffix && face.main.length > 4 ? Math.round(base * 0.8) : base;
  return (
    <Card tone={1} className="flex flex-col px-6 py-5">
      <div className="flex justify-between text-[15px]">
        <div>{greeting(ctx.clock.hour)}</div>
        <div className="text-[13px] text-muted">{ctx.boot.household.location.name.split(',')[0]}</div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col justify-center">
        <div className="flex items-baseline">
          <div className="tabular font-semibold leading-none tracking-[-0.03em]" style={{ fontSize: size }}>
            {face.main}
          </div>
          {face.suffix ? <div className="ml-2 text-[24px] font-semibold">{face.suffix}</div> : null}
        </div>
        <div className="mt-2 text-[16px] text-muted">{longDate(ctx.clock.dateKey)}</div>
        {narrow ? null : <ClockStatus />}
      </div>
    </Card>
  );
}

function NowCard({ tall }: { tall: boolean }) {
  const ctx = useDisplay();
  const { w, pending } = useWeatherState();
  const unit = ctx.prefs.unit;
  if (!w) {
    return (
      <Card tone={2}>
        <Unavailable>{pending ? 'Fetching the weather…' : 'Weather unavailable right now.'}</Unavailable>
      </Card>
    );
  }
  const temps = w.hourly.map((h) => h.temp_c);
  const sp = sparkline(temps, ctx.clock.hour + ctx.clock.minute / 60);
  const T = (c: number) => toUnit(c, unit);
  return (
    <Card tone={2} className="flex flex-col overflow-hidden px-[22px] py-4">
      <div className="flex items-center">
        <div className="mr-[18px] flex-none">
          <WeatherIcon kind={w.current.kind} size={50} isDay={w.current.is_day} />
        </div>
        <div className="min-w-0">
          <div className="flex items-start">
            <div className="text-[54px] font-light leading-none tracking-[-0.03em]">{T(w.current.temp_c)}°</div>
            <div className="ml-[2px] mt-[6px] text-[20px]">{unit}</div>
          </div>
          <div className="ellipsis mt-1 text-[14px] text-muted">
            {w.current.text} · Feels {T(w.current.feels_like_c)}°
          </div>
        </div>
      </div>
      <div className="mt-auto pt-2">
        <div className="mb-1 flex justify-between text-[12px] text-muted">
          <div>Today, hourly</div>
          <div>
            H {T(sp.max)}° · L {T(sp.min)}°
          </div>
        </div>
        <Sparkline sp={sp} height={tall ? 80 : 40} />
        <div className="mt-[3px] flex justify-between text-[12px] text-muted">
          {['00', '06', '12', '18', '24'].map((h) => (
            <div key={h}>{h}</div>
          ))}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 border-t border-line pt-2">
        <div>
          <div className="text-[12px] text-muted">Outdoor</div>
          <div className="text-[20px]">{T(w.current.temp_c)}°</div>
          <div className="text-[12px] text-muted">{w.current.humidity}% RH</div>
        </div>
        <div className="border-l border-line pl-[14px]">
          <div className="text-[12px] text-muted">Indoor</div>
          <div className="text-[20px]">—</div>
          <div className="text-[12px] text-muted">No sensor yet</div>
        </div>
        <div className="border-l border-line pl-[14px]">
          <div className="text-[12px] text-muted">Daylight</div>
          <div className="text-[14px] leading-[1.45]">↑ {formatTime(w.today.sunrise, ctx.prefs.h24)}</div>
          <div className="text-[14px] leading-[1.45]">↓ {formatTime(w.today.sunset, ctx.prefs.h24)}</div>
        </div>
      </div>
    </Card>
  );
}

function Sparkline({ sp, height }: { sp: ReturnType<typeof sparkline>; height: number }) {
  const scaleY = height / 40;
  return (
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 240 40" preserveAspectRatio="none" className="cover h-full w-full overflow-visible" aria-hidden>
        <defs>
          <linearGradient id="obvTempFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" style={{ stopColor: 'var(--chartFillA)' }} />
            <stop offset="1" style={{ stopColor: 'var(--chartFillB)' }} />
          </linearGradient>
        </defs>
        <polygon points={sp.area} fill="url(#obvTempFill)" />
        <polyline points={sp.line} fill="none" strokeWidth={2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--chartLine)' }} />
      </svg>
      <div className="absolute bottom-0 top-0 w-px" style={{ left: `${sp.nowX}%`, background: 'var(--bd)' }} />
      <div
        className="absolute h-[9px] w-[9px] rounded-full"
        style={{ left: `${sp.nowX}%`, top: sp.nowY * scaleY, margin: '-4.5px 0 0 -4.5px', background: 'var(--chartLine)', boxShadow: '0 0 0 2px var(--cardSolid)' }}
      />
    </div>
  );
}

function bandStyle(band: string | null | undefined): CSSProperties {
  if (band === 'good') return { background: 'var(--goodBg)', color: 'var(--goodFg)' };
  return { background: 'var(--now)', color: 'var(--onInk)' };
}

function AirCard({ big, compact }: { big: boolean; compact: boolean }) {
  const ctx = useDisplay();
  const { w } = useWeatherState();
  if (!w) {
    return (
      <Card tone={3}>
        <Unavailable>Air readings will appear here.</Unavailable>
      </Card>
    );
  }
  const a = w.air;
  const metrics = [
    { k: 'Wind', v: formatWind(w.metrics.wind_ms, w.metrics.wind_dir) },
    { k: 'UV index', v: `${Math.round(w.today.uv_index)} · ${w.today.uv_band}` },
    { k: 'Precipitation', v: `${Math.round(w.today.precip_mm * 10) / 10} mm` },
    { k: 'Visibility', v: formatVisibility(w.metrics.visibility_m) },
    { k: 'Pressure', v: formatPressure(w.metrics.pressure_hpa) },
    { k: 'Humidity', v: `${w.current.humidity}%` },
  ];
  const num = big ? 'text-[52px]' : 'text-[38px]';
  return (
    <Card tone={3} className="flex flex-col px-6 py-[18px]">
      <h2 className="text-[17px] font-normal">The air around you</h2>
      <div className="mt-[14px] grid grid-cols-3">
        <div>
          <div className="text-[12px] tracking-[0.08em] text-muted">AIR QUALITY</div>
          <div className={`mt-1 font-light ${num}`}>{a.aqi !== null && a.aqi !== undefined ? a.aqi : '—'}</div>
          {a.band_label ? (
            <div className="mt-[2px] inline-block rounded-[5px] px-[7px] py-[3px] text-[12px]" style={bandStyle(a.band)}>
              {a.band_label}
            </div>
          ) : null}
        </div>
        <div className="border-l border-line pl-4">
          <div className="text-[12px] tracking-[0.08em] text-muted">PM2.5</div>
          <div className={`mt-1 font-light ${num}`}>{a.pm2_5 !== null && a.pm2_5 !== undefined ? Math.round(a.pm2_5) : '—'}</div>
          <div className="mt-1 text-[12px] text-muted">µg/m³</div>
        </div>
        <div className="border-l border-line pl-4">
          <div className="text-[12px] tracking-[0.08em] text-muted">POLLEN</div>
          <div className={`mt-1 font-light ${num}`}>—</div>
          <div className="mt-1 text-[12px] text-muted">{compact ? 'N/A here' : 'Not available here'}</div>
        </div>
      </div>
      <div className="mt-auto grid" style={{ gridColumnGap: 24, gridTemplateColumns: compact ? 'minmax(0,1fr)' : 'repeat(2, minmax(0,1fr))' }}>
        {metrics.map((m) => (
          <div key={m.k} className="flex h-[36px] items-center justify-between border-t border-line text-[13px]" style={{ height: big ? 44 : compact ? 28 : 36 }}>
            <div className="text-muted2">{m.k}</div>
            <div className="font-medium">{m.v}</div>
          </div>
        ))}
      </div>
      <span className="sr-only">Readings for {ctx.boot.household.location.name}</span>
    </Card>
  );
}

/** Seven forecast rows on the week's own min–max scale. `dense` fits them under other content. */
function WeekRows({ daily, compact, dense = false }: { daily: Weather['daily']; compact: boolean; dense?: boolean }) {
  const ctx = useDisplay();
  const bars = weekBars(daily);
  const T = (c: number) => toUnit(c, ctx.prefs.unit);
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-between" style={{ marginTop: dense ? 4 : 8 }}>
      {daily.map((d: Weather['daily'][number], i) => (
        <div
          key={d.date}
          className="flex flex-1 items-center border-t"
          style={{ borderColor: 'var(--line2)', minHeight: dense ? 20 : 28, fontSize: dense ? 13 : 14 }}
        >
          <div className="flex-none" style={{ width: compact ? 42 : 52, fontWeight: i === 0 ? 600 : 400 }}>
            {shortWeekday(d.date, ctx.clock.dateKey)}
          </div>
          <div className="w-[26px] flex-none" style={{ marginLeft: compact ? 0 : 4 }}>
            <WeatherIcon kind={d.kind} size={dense ? 18 : 22} />
          </div>
          <div className="flex-none text-[12px] text-rain" style={{ width: compact ? 28 : 34, marginLeft: compact ? 2 : 8 }}>
            {precipLabel(d.precip_prob)}
          </div>
          <div className="flex-none text-right text-muted" style={{ width: compact ? 26 : 30, marginLeft: compact ? 0 : 4 }}>
            {T(d.min_c)}°
          </div>
          <div className="relative h-1 min-w-[24px] flex-1 rounded-[2px]" style={{ background: 'var(--track)', margin: compact ? '0 6px' : '0 10px' }}>
            <div className="absolute bottom-0 top-0 rounded-[2px]" style={{ left: `${bars[i]!.left}%`, width: `${bars[i]!.width}%`, background: 'var(--barFill)' }} />
          </div>
          <div className="flex-none font-medium" style={{ width: compact ? 26 : 30 }}>
            {T(d.max_c)}°
          </div>
        </div>
      ))}
    </div>
  );
}

function WeekCard({ compact = false }: { compact?: boolean }) {
  const { w } = useWeatherState();
  if (!w) {
    return (
      <Card tone={4}>
        <Unavailable>The week ahead will appear here.</Unavailable>
      </Card>
    );
  }
  return (
    <Card tone={4} className="flex flex-col px-[22px] py-[18px]">
      <div className="flex items-baseline justify-between">
        <h2 className="text-[17px] font-normal">The week ahead</h2>
        <div className="text-[12px] text-muted">Low — High</div>
      </div>
      <WeekRows daily={w.daily} compact={compact} />
    </Card>
  );
}

/** Today's conditions on top, the week ahead below — one card instead of two. */
function ForecastCard({ narrow = false }: { narrow?: boolean }) {
  const ctx = useDisplay();
  const { w, pending } = useWeatherState();
  if (!w) {
    return (
      <Card tone={2}>
        <Unavailable>{pending ? 'Fetching the weather…' : 'Weather unavailable right now.'}</Unavailable>
      </Card>
    );
  }
  const T = (c: number) => toUnit(c, ctx.prefs.unit);
  const today = w.daily[0];
  const details = `Feels ${T(w.current.feels_like_c)}°${today ? ` · H ${T(today.max_c)}° L ${T(today.min_c)}°` : ''}`;
  return (
    <Card tone={2} className="flex flex-col overflow-hidden px-5 py-4">
      <div className="flex flex-none items-center">
        <div className="mr-3 flex-none">
          <WeatherIcon kind={w.current.kind} size={narrow ? 40 : 50} isDay={w.current.is_day} />
        </div>
        <div className="flex items-start">
          <div className="font-light leading-none tracking-[-0.03em]" style={{ fontSize: narrow ? 44 : 56 }}>
            {T(w.current.temp_c)}°
          </div>
          <div className="ml-[2px] mt-1 text-[18px]">{ctx.prefs.unit}</div>
        </div>
        <div className="ml-4 min-w-0 flex-1 text-right">
          <div className="ellipsis text-[14px]">{w.current.text}</div>
          {narrow ? null : <div className="ellipsis mt-[2px] text-[12px] text-muted">{details}</div>}
        </div>
      </div>
      {/* Narrow slots have no room beside the temperature; give the details their own line. */}
      {narrow ? <div className="ellipsis mt-1 flex-none text-[12px] text-muted">{details}</div> : null}
      <div className="mt-3 flex flex-none items-baseline justify-between">
        <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">The week ahead</div>
        <div className="text-[12px] text-muted">Low — High</div>
      </div>
      <WeekRows daily={w.daily} compact={narrow} dense />
    </Card>
  );
}
